'use client';
import { Bot, Clock, Globe, KeyRound, MessageSquare, SquareTerminal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSurfaceLabel } from '../../lib/i18n/surfaceLabel';

/** Surfaces that are not plugin-provided platforms. They get a plain glyph and never probe the plugin icon
 *  route, since a delegated run, scheduled job, web app and terminal have no brand asset to resolve. */
const GLYPHS: Record<string, typeof Bot> = {
  subagent: Bot, cron: Clock, web: Globe, cli: SquareTerminal, internal: Bot, api: KeyRound,
};

/** The mark for a conversation's platform: an icon served by that platform plugin, or a neutral glyph when
 *  the plugin is disabled, unknown, or has no icon. The route is intentionally resolved at render time so
 *  installed plugins can provide their own assets without a core roster or copied static files. */
export function PlatformIcon({ platform, size = 14, className = '' }: { platform: string; size?: number; className?: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [platform]);

  // The same metadata name every other surface uses; a pending or failed projection leaves the recorded id.
  const title = useSurfaceLabel()(platform);
  const hasPluginIcon = !Object.hasOwn(GLYPHS, platform);
  if (hasPluginIcon && !failed) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={`/api/plugins/${encodeURIComponent(platform)}/icon`}
        alt={title}
        title={title}
        className={`shrink-0 object-contain ${className}`}
        style={{ width: size, height: size }}
        onError={() => setFailed(true)}
      />
    );
  }

  const Glyph = GLYPHS[platform] ?? MessageSquare;
  return (
    <span role="img" aria-label={title} title={title} className="inline-flex shrink-0">
      <Glyph size={size} className={`text-muted-foreground ${className}`} aria-hidden />
    </span>
  );
}
